import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/authorities';
import { db } from '../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import Loader from '../Loader';

interface AuthorizationGuardProps {
    children: React.ReactNode;
    requiredRoles?: UserRole[];
    requiredAuthorities?: string[];
    fallbackPath?: string;
    moduleKey?: string;
}

const AuthorizationGuard: React.FC<AuthorizationGuardProps> = ({
    children,
    requiredRoles = [],
    requiredAuthorities = [],
    fallbackPath = '/dashboard',
    moduleKey
}) => {
    const { user } = useAuth();
    const [modules, setModules] = React.useState<Record<string, boolean>>({});
    const [loadingModules, setLoadingModules] = React.useState(!!moduleKey);

    React.useEffect(() => {
        if (!moduleKey) return;
        const docRef = doc(db, 'tenantSettings', 'modules');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setModules(docSnap.data() as Record<string, boolean>);
            }
            setLoadingModules(false);
        }, (err) => {
            console.error('Error fetching module status in guard:', err);
            setLoadingModules(false);
        });
        return () => unsubscribe();
    }, [moduleKey]);

    const isModuleDisabled = React.useMemo(() => {
        if (!moduleKey) return false;
        const config = modules[moduleKey] as any;
        if (config === undefined || config === null) {
            if (moduleKey === 'Notifications') return true;
            return false;
        }
        if (typeof config === 'boolean') return config === false;
        if (typeof config === 'object' && config !== null) return config.enabled === false;
        return false;
    }, [moduleKey, modules]);

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (user.role === 'super_admin') {
        return <>{children}</>;
    }

    if (loadingModules) {
        return <Loader />;
    }

    if (moduleKey && isModuleDisabled) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center border border-slate-200">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Module Deactivated</h2>
                    <p className="text-gray-600 mb-6 text-sm">
                        This module has been deactivated by the system administrator. Please contact your coordinator or Dynamix Zone for activation options.
                    </p>
                    <button
                        onClick={() => window.history.back()}
                        className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-bold text-sm shadow-md"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // Check role-based access
    if (requiredRoles.length > 0 && !requiredRoles.includes(user.role as UserRole)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-6">
                        You don't have permission to access this page. Your role: <span className="font-semibold">{user.role}</span>
                    </p>
                    <button
                        onClick={() => window.history.back()}
                        className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // Check authority-based access (if user has authorities field)
    if (requiredAuthorities.length > 0 && user.authorities) {
        const hasRequiredAuthority = requiredAuthorities.some(authority =>
            user.authorities && user.authorities[authority]
        );

        if (!hasRequiredAuthority) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                        <p className="text-gray-600 mb-6">
                            You don't have the required permissions to access this page.
                        </p>
                        <button
                            onClick={() => window.history.back()}
                            className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            );
        }
    }

    return <>{children}</>;
};

export default AuthorizationGuard;
